export type PolicyCategory =
  | "core"
  | "safeguarding"
  | "health-safety"
  | "clinical"
  | "people"
  | "environment"
  | "governance";

export interface PolicySection {
  title: string;
  lines: string[];
}

export interface PolicyDefinition {
  id: string;
  code: string;
  title: string;
  category: PolicyCategory;
  cqcFocus: string;
  sop: boolean;
  version: string;
  reviewMonths: number;
  summary: string;
  keyPoints: string[];
  relatedRegulations?: string[];
  sections?: PolicySection[];
}

export interface PolicyCategoryMeta {
  id: PolicyCategory;
  label: string;
  shortLabel: string;
  description: string;
}

export interface PolicyQuizOption {
  label: string;
  correct: boolean;
}

export interface PolicyQuizQuestion {
  id: string;
  prompt: string;
  options: PolicyQuizOption[];
}

export interface PolicyReadingPage {
  pageNumber: number;
  title: string;
  lines: string[];
}

export function categoryLabel(category: PolicyCategory): string {
  switch (category) {
    case "core": return "Core";
    case "safeguarding": return "Safeguarding";
    case "health-safety": return "Health and Safety";
    case "clinical": return "Clinical";
    case "people": return "People";
    case "governance": return "Governance";
  }
}

export function buildPolicyPages(policy: PolicyDefinition): PolicyReadingPage[] {
  const pages: PolicyReadingPage[] = [];

  pages.push({
    pageNumber: 1,
    title: "Policy cover",
    lines: [
      policy.title,
      `Reference: ${policy.code}`,
      `Category: ${categoryLabel(policy.category)}`,
      `Version: ${policy.version}`,
      `Review period: ${policy.reviewMonths} months`,
      policy.sop ? "Document type: SOP" : "Document type: Policy",
    ],
  });

  pages.push({
    pageNumber: 2,
    title: "Purpose and scope",
    lines: [
      policy.summary,
      "This policy applies to all staff, agency staff, students and volunteers at Roop Cottage Residential Care Home.",
      "It must be read together with related policies and CQC requirements.",
    ],
  });

  if (policy.sections && policy.sections.length > 0) {
    for (const section of policy.sections) {
      pages.push({ pageNumber: pages.length + 1, title: section.title, lines: section.lines });
    }
  } else {
    for (let i = 0; i < policy.keyPoints.length; i += 3) {
      pages.push({ pageNumber: pages.length + 1, title: "Key requirements", lines: policy.keyPoints.slice(i, i + 3) });
    }
  }

  if (policy.relatedRegulations && policy.relatedRegulations.length > 0) {
    pages.push({ pageNumber: pages.length + 1, title: "Legal and regulatory references", lines: policy.relatedRegulations });
  }

  pages.push({
    pageNumber: pages.length + 1,
    title: "Declaration",
    lines: [
      "Read each section carefully.",
      "Complete the knowledge check.",
      "Confirm your declaration.",
      "Your sign off will be recorded in the policy log.",
    ],
  });

  return pages;
}
